import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  ArrowRight,
  BarChart3,
  BatteryCharging,
  ClipboardList,
  LayoutGrid,
  MapPin,
  Search,
  Settings,
  Sliders,
  Sun,
  Table,
  Users,
} from "lucide-react";
import type { ViewDefinition, ViewId } from "../../lib/views.js";
import { Modal } from "../Modal.js";

interface Props {
  views: readonly ViewDefinition[];
  onClose: () => void;
  onNavigate: (path: string) => void;
  onCustomize: () => void;
}

interface Entry {
  title: string;
  desc: string;
  icon: React.ReactNode;
  run: () => void;
}

/** One icon per section, as the design gives its rows. */
const ICONS: Record<ViewId, React.ReactNode> = {
  dashboard: <LayoutGrid size={18} />,
  solar: <Sun size={18} />,
  battery: <BatteryCharging size={18} />,
  analytics: <BarChart3 size={18} />,
  live: <Activity size={18} />,
  telemetry: <Table size={18} />,
  site: <MapPin size={18} />,
  settings: <Settings size={18} />,
  sessions: <Users size={18} />,
  audit: <ClipboardList size={18} />,
};

/**
 * The design's command palette, over this app's actual sections.
 *
 * The design lists seven fixed rows describing a different installation -- an
 * eighteen-panel roof schematic, a 13.5 kWh PowerPack, a financial ROI ledger
 * -- and filters them by substring. The shape is kept and the contents come
 * from `lib/views.ts`, so a section this account may not see is not offered,
 * and a section added later appears here without anyone remembering to add it.
 *
 * Ctrl+K opens it, which the design advertised in a tooltip and never bound.
 */
export function SearchModal({ views, onClose, onNavigate, onCustomize }: Props) {
  const [query, setQuery] = useState("");
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    field.current?.focus();
  }, []);

  const entries = useMemo<Entry[]>(() => {
    const sections = views.map((view) => ({
      title: view.label,
      desc: `Open ${view.path}`,
      icon: ICONS[view.id],
      run: () => {
        onNavigate(view.path);
      },
    }));
    return [
      ...sections,
      {
        title: "Customise the dashboard",
        desc: "Toggle widgets, reorder them, change the accent and density",
        icon: <Sliders size={18} />,
        run: onCustomize,
      },
    ];
  }, [views, onNavigate, onCustomize]);

  const needle = query.trim().toLowerCase();
  const matches =
    needle === ""
      ? entries
      : entries.filter(
          (entry) =>
            entry.title.toLowerCase().includes(needle) || entry.desc.toLowerCase().includes(needle),
        );

  return (
    <Modal title="Search" onClose={onClose} className="md:max-w-xl">
      <div className="flex items-center gap-3 border-b border-line pb-4">
        <Search size={20} aria-hidden="true" className="shrink-0 text-ink-muted" />
        <input
          type="search"
          // Focused from an effect rather than with `autoFocus`. The rule
          // against the attribute is about pages, where it moves focus before
          // the reader has oriented; inside a dialog opened deliberately from
          // a search button, landing in the field is the whole point.
          ref={field}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          placeholder="Search sections and actions"
          aria-label="Search sections and actions"
          className="w-full bg-transparent text-base text-ink outline-none placeholder:text-ink-faint"
        />
      </div>

      {matches.length === 0 ? (
        <p className="py-8 text-center text-sm text-ink-muted">{`Nothing matches “${query}”.`}</p>
      ) : (
        <ul className="mt-4 max-h-80 space-y-1.5 overflow-y-auto pr-1">
          {matches.map((entry) => (
            <li key={entry.title}>
              <button
                type="button"
                onClick={() => {
                  entry.run();
                  onClose();
                }}
                className="group flex w-full items-center justify-between gap-3 rounded-2xl p-3 text-left transition-colors hover:bg-line"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="rounded-xl bg-line p-2 text-ink-muted transition-colors group-hover:bg-btn group-hover:text-btn-ink"
                  >
                    {entry.icon}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-ink">{entry.title}</span>
                    <span className="block truncate text-xs text-ink-muted">{entry.desc}</span>
                  </span>
                </span>
                <ArrowRight
                  size={16}
                  aria-hidden="true"
                  className="shrink-0 text-ink-muted transition-transform group-hover:translate-x-1"
                />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
